import {
   Prescription,
   PrescriptionPackage as PrescriptionPackageApi,
} from '@dreryk/egabinet-prescription-frontend-api';
import {
   VisitPrescriptionListDraftUpdateCommand,
   VisitPrescriptionPackageQuery,
} from '@dreryk/egabinet-visit-frontend-api';

import { performCqrsQuerySearch, useCqrsCommand } from '@dreryk/command-query-react-common';
import { ActionEventInterceptor, dispatchSnackbarNegativeEvent } from '@dreryk/react-components';
import { PromiseVoidFunction } from '@dreryk/react-common';

import { getDraftDisplayIndex } from '~libs.shared.prescription';

import { historicalVisitTranslationKeys } from '../../../../translations';
import {
   MedicineReplacementFormBean,
   MedicineReplacementFormController,
} from '../../../../features/prescriptions/medicineReplacementModal/form/MedicineReplacementForm.types';

import {
   createReplacementPrescription,
   mapPackagePrescriptionToDraft,
} from './NotCopiedPrescriptionReplacementActionInterceptor.utils';

const t = historicalVisitTranslationKeys.copyPrescription;

type NotCopiedPrescriptionReplacementActionInterceptorProps = Pick<
   MedicineReplacementFormController,
   'handleSubmit'
> & {
   readonly children: JSX.Element;
   readonly targetVisitId: number;
   readonly count: number;
   readonly onSuccess: VoidFunction | PromiseVoidFunction;
};

type UpdateDraftPrescriptionsParams = {
   readonly prescriptions: Prescription[];
};

export const NotCopiedPrescriptionReplacementActionInterceptor = (
   props: NotCopiedPrescriptionReplacementActionInterceptorProps
): JSX.Element => {
   const { executeAsync: updateDraftPrescriptions } = useCqrsCommand<void, UpdateDraftPrescriptionsParams>(
      (parameters) => {
         const command = new VisitPrescriptionListDraftUpdateCommand();
         command.visitId = props.targetVisitId;
         command.prescriptions = parameters.prescriptions;
         return command;
      },
      {
         onSuccess: async () => {
            await props.onSuccess();
         },
      }
   );

   const handleReplace = async (formBean: MedicineReplacementFormBean): Promise<void> => {
      if (!formBean.newMedicine) {
         return;
      }

      try {
         const prescriptionPackages = await performCqrsQuerySearch<PrescriptionPackageApi>(() => {
            const query = new VisitPrescriptionPackageQuery();
            query.visitId = props.targetVisitId;
            return query;
         });

         const existingPrescriptions: Prescription[] = prescriptionPackages.flatMap((prescriptionPackage) =>
            (prescriptionPackage.prescriptions ?? []).map(mapPackagePrescriptionToDraft)
         );

         const newMedicineId = formBean.newMedicine.medicine.id;

         const newPrescriptions: Prescription[] = Array.from({ length: props.count }, (__, idx) =>
            createReplacementPrescription(newMedicineId, getDraftDisplayIndex(existingPrescriptions, props.count, idx))
         );

         await updateDraftPrescriptions({
            prescriptions: [...existingPrescriptions, ...newPrescriptions],
         });
      } catch {
         dispatchSnackbarNegativeEvent(t.replacementError);
      }
   };

   return <ActionEventInterceptor action={props.handleSubmit(handleReplace)}>{props.children}</ActionEventInterceptor>;
};
